/**
 * Documents V2 Utilities
 *
 * Helper functions for document display and formatting.
 */

import { FileText, File, Image as ImageIcon } from 'lucide-react';
import {
  CORE_SLOT_LABELS,
  FILE_TYPES,
  FileTypeSlug
} from '@/lib/services/files/file-types';
import type { CoreDocumentType } from './types';

/**
 * Format file size from bytes to human-readable string
 */
export function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${Math.round(bytes / 1024)} KB`;
}

/**
 * Format date string to Australian locale format
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}

/**
 * Get the appropriate Lucide icon component for a file type
 */
export function getFileIcon(fileType: string) {
  const normalizedType = fileType.toLowerCase();

  switch (normalizedType) {
    case 'pdf':
    case 'application/pdf':
      return FileText;
    case 'docx':
    case 'doc':
    case 'application/msword':
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      return File;
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'webp':
    case 'image/jpeg':
    case 'image/png':
    case 'image/gif':
    case 'image/webp':
      return ImageIcon;
    default:
      return FileText;
  }
}

/**
 * Get icon color class for a file type
 */
export function getFileIconColor(fileType: string): string {
  const normalizedType = fileType.toLowerCase();

  if (normalizedType.includes('pdf')) {
    return 'text-error';
  }
  if (normalizedType.includes('doc')) {
    return 'text-info';
  }
  if (
    normalizedType.includes('jpg') ||
    normalizedType.includes('jpeg') ||
    normalizedType.includes('png') ||
    normalizedType.includes('image')
  ) {
    return 'text-success';
  }
  return 'text-muted-foreground';
}

/**
 * Get human-readable label for a core document type.
 *
 * Reads `FILE_TYPES` (via `CORE_SLOT_LABELS`) rather than restating the names:
 * the "Mark as …" menu items and the categorise toast name the same slots, and a
 * second hand-typed copy is how one surface ends up calling a document something
 * the other does not (VALE-806).
 */
export function getCoreDocumentTypeLabel(type: CoreDocumentType): string {
  if (type === 'codicil') return FILE_TYPES[FileTypeSlug.CODICIL].name;
  return CORE_SLOT_LABELS[type] ?? 'Document';
}

/**
 * Check if a file type is an image
 */
export function isImageFileType(fileType: string): boolean {
  const normalizedType = fileType.toLowerCase();
  return (
    normalizedType.includes('jpg') ||
    normalizedType.includes('jpeg') ||
    normalizedType.includes('png') ||
    normalizedType.includes('gif') ||
    normalizedType.includes('webp') ||
    normalizedType.includes('image/')
  );
}

/**
 * Check if a file type is a PDF
 */
export function isPdfFileType(fileType: string): boolean {
  const normalizedType = fileType.toLowerCase();
  return normalizedType.includes('pdf');
}

/**
 * Generate thumbnail URL for a document
 * Uses the backend thumbnail endpoint through the API proxy
 */
export function getThumbnailUrl(documentId: number): string {
  return `/api/files/${documentId}/thumbnail`;
}

/**
 * Check if a document type is a core document
 */
export function isCoreDocumentType(fileType: string): boolean {
  const coreTypes = [
    'will',
    'codicil',
    'death-certificate',
    'grant-of-representation',
    'grant-of-probate'
  ];
  return coreTypes.includes(fileType.toLowerCase());
}
